import { useCallback, useEffect, useState } from 'react';
import { loadCaseVisibility } from '../services/dataService';
import type { CaseInfo } from '../types';

interface UseCaseLibraryVisibilityResult {
  visibleCaseIds: string[];
  setVisibleCaseIds: (caseIds: string[]) => void;
  getVisibleCases: (allCases: CaseInfo[]) => CaseInfo[];
  isCaseVisible: (caseId: string) => boolean;
  toggleCaseVisibility: (caseId: string) => void;
}

// PE Experiment: Replace old Episode 1 with EXPERIMENT 2 Episode 1 (updated images & items)
const CASE_ID_OVERRIDES: Record<string, string> = {
  'a1ec7faf-d92f-4c14-9abb-6bc45989b6cf':
    '8165053e-e3d7-462a-a672-27091eb2ab34',
};

const applyCaseIdOverrides = (caseIds: string[]): string[] => {
  return caseIds.map((id) => CASE_ID_OVERRIDES[id] || id);
};

const STORAGE_KEY = 'caseLibraryVisibility';
const _DEBOUNCE_DELAY = 1000; // 1 second debounce for data service saves

/**
 * Hook for managing which cases are visible in the Case Library with data sync
 */
export const useCaseLibraryVisibility = (): UseCaseLibraryVisibilityResult => {
  const [visibleCaseIds, setVisibleCaseIdsState] = useState<string[]>([]);

  // Load visibility settings from data service first, then localStorage fallback
  useEffect(() => {
    const loadVisibility = async () => {
      try {
        // Try data service first
        const remoteData = await loadCaseVisibility();
        if (remoteData) {
          const overriddenData = applyCaseIdOverrides(remoteData);
          setVisibleCaseIdsState(overriddenData);
          // Update localStorage as backup
          localStorage.setItem(STORAGE_KEY, JSON.stringify(overriddenData));
          return;
        }

        // Fallback to localStorage
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const caseIds = JSON.parse(saved);
          setVisibleCaseIdsState(caseIds);
        }
      } catch (error) {
        console.error('Error loading case visibility settings:', error);
        // Try localStorage as final fallback
        try {
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const caseIds = JSON.parse(saved);
            setVisibleCaseIdsState(caseIds);
          }
        } catch (localError) {
          console.error('Error loading from localStorage:', localError);
        }
      }
    };

    loadVisibility();
  }, []);

  // Save to localStorage
  const setVisibleCaseIds = useCallback((caseIds: string[]) => {
    try {
      // Save to localStorage immediately
      localStorage.setItem(STORAGE_KEY, JSON.stringify(caseIds));
      setVisibleCaseIdsState(caseIds);
    } catch (error) {
      console.error('Error saving case visibility settings:', error);
    }
  }, []);

  // Filter cases based on visibility settings
  const getVisibleCases = useCallback(
    (allCases: CaseInfo[]): CaseInfo[] => {
      // If no visibility settings exist, show all cases (backward compatibility)
      if (visibleCaseIds.length === 0) {
        return allCases;
      }
      return allCases.filter((caseInfo) =>
        visibleCaseIds.includes(caseInfo.caseId),
      );
    },
    [visibleCaseIds],
  );

  // Check if a specific case is visible
  const isCaseVisible = useCallback(
    (caseId: string): boolean => {
      // If no visibility settings exist, all cases are visible (backward compatibility)
      if (visibleCaseIds.length === 0) {
        return true;
      }
      return visibleCaseIds.includes(caseId);
    },
    [visibleCaseIds],
  );

  // Toggle visibility of a specific case
  const toggleCaseVisibility = useCallback(
    (caseId: string) => {
      const newVisibleCaseIds = visibleCaseIds.includes(caseId)
        ? visibleCaseIds.filter((id) => id !== caseId)
        : [...visibleCaseIds, caseId];

      setVisibleCaseIds(newVisibleCaseIds);
    },
    [visibleCaseIds, setVisibleCaseIds],
  );

  return {
    visibleCaseIds,
    setVisibleCaseIds,
    getVisibleCases,
    isCaseVisible,
    toggleCaseVisibility,
  };
};
